// Brand Research screen.
//
// Three modes:
//   input    → URL form + existing-research grid
//   progress → 8-step SSE-driven progress card
//   result   → V02 "Insights" view: 4 KPI cards + quick-facts strip + drill-down
//              accordion (angles / gaps / reputation / audience)
//
// V02 surfaces five fields the backend doesn't yet return — brand score,
// per-angle strength + tags, per-gap action label, reputation empty-state
// note. Each has a client-side fallback below; replace once the backend
// returns them.

import Beacon from '../components/Beacon.jsx';
import { useEffect, useRef, useState } from 'react';
import { PALETTE } from '../palette.js';
import { Card, Pill, Button } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import { api, getToken } from '../api.js';

const STEP_LABELS = [
  'Starting brand research',
  'Connecting to research agent',
  'Fetching website',
  'Searching for reviews',
  'Analyzing brand',
  'Processing data',
  'Saving files',
  'Saving research',
  'Done',
];

const brandResearchResponsiveStyles = `
  @media (max-width: 800px) {
    .brand-research-page { padding: 24px 18px 48px !important; }
    .brand-research-page form.brand-research-form { flex-direction: column; }
    .brand-research-page form.brand-research-form > * { width: 100%; min-width: 0 !important; box-sizing: border-box; }
    .brand-research-result { padding: 22px 18px 36px !important; }
    .brand-research-result-header { align-items: stretch !important; flex-direction: column; gap: 14px !important; }
    .brand-research-result-identity { flex-basis: auto !important; width: 100%; align-items: flex-start !important; }
    .brand-research-result-title { font-size: 30px !important; overflow-wrap: anywhere; }
    .brand-research-result-meta { overflow-wrap: anywhere; }
    .brand-research-result-actions {
      width: 100%;
      display: grid !important;
      grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    }
    .brand-research-result-actions > button { width: 100%; justify-content: center; }
    .brand-research-insight-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
    .brand-research-two-column { grid-template-columns: minmax(0, 1fr) !important; }
    .brand-research-gap-row { grid-template-columns: 20px minmax(0, 1fr) !important; }
    .brand-research-gap-row > :nth-child(3),
    .brand-research-gap-row > :nth-child(4) { grid-column: 2; }
  }
  @media (max-width: 520px) {
    .brand-research-insight-grid { grid-template-columns: minmax(0, 1fr) !important; }
  }
`;

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

export function canGenerateSaasBrandResearch(session, companyId = activeCompanyId(session)) {
  const role = session?.user?.role
    || session?.user?.memberships?.find((membership) => membership.companyId === companyId)?.role
    || null;
  return ['owner', 'admin', 'marketer'].includes(String(role || '').toLowerCase());
}

export function normalizeSaasResearchResult(research = {}) {
  return {
    ...(research.data || {}),
    slug: research.brand?.slug || '',
    _source: research.sourceUrl || research.brand?.websiteUrl || '',
    _saasResearch: {
      id: research.id,
      generatedAt: research.generatedAt,
      artifacts: research.artifacts || {},
      brand: research.brand || null,
    },
  };
}

export function safeBrandResearchError(error, fallback = 'Brand research could not be completed. Try again.') {
  const code = String(error?.code || '').toLowerCase();
  if (code === 'platform_cost_guardrail_paused') return 'Brand research is temporarily paused by the platform team.';
  if (code === 'research_url_not_allowed' || code === 'research_url_unresolvable') {
    return 'Enter a public HTTPS website that can be reached from the internet.';
  }
  if (code === 'brand_not_found' || code === 'brand_research_not_found') {
    return 'That brand research is not available in the active company.';
  }
  if (Number(error?.status) === 403) return 'Your company role cannot perform this brand research action.';
  return fallback;
}

export default function BrandResearch({ navigate, route, session }) {
  const [mode, setMode] = useState('input');
  const [brands, setBrands] = useState(null);
  const [researches, setResearches] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [url, setUrl] = useState('');
  const [progress, setProgress] = useState({ label: 'Starting…', steps: {} });
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const abortRef = useRef(null);
  const companyId = activeCompanyId(session);
  const isSaasSession = session?.user?.auth?.type === 'supabase';
  const canGenerate = !isSaasSession || canGenerateSaasBrandResearch(session, companyId);

  useEffect(() => {
    let active = true;
    setBrands(null);
    setResearches(isSaasSession ? null : []);
    if (isSaasSession) {
      Promise.all([
        api.get('/api/saas/brands', tenantHeaders(companyId)),
        api.get('/api/saas/brand-research', tenantHeaders(companyId)),
      ]).then(([brandResponse, researchResponse]) => {
        if (!active) return;
        const nextBrands = brandResponse.brands || [];
        setBrands(nextBrands);
        setResearches(researchResponse.researches || []);
        setSelectedBrandId((current) => current || nextBrands[0]?.id || '');
        const first = nextBrands[0];
        setUrl((current) => current || first?.websiteUrl || '');
      }).catch((err) => {
        if (!active) return;
        setBrands([]);
        setResearches([]);
        setError(safeBrandResearchError(err, 'Brand research could not be loaded. Try again.'));
      });
    } else {
      api.get('/api/clients')
        .then((response) => { if (active) setBrands(response.clients || []); })
        .catch(() => { if (active) setBrands([]); });
    }
    return () => { active = false; };
  }, [isSaasSession, companyId]);

  useEffect(() => {
    if (isSaasSession) {
      const researchId = route?.query?.research;
      if (!researchId) return;
      setMode('progress');
      setProgress({ label: 'Loading saved research…', steps: {} });
      api.get(`/api/saas/brand-research/${encodeURIComponent(researchId)}`, tenantHeaders(companyId))
        .then((response) => {
          setData(normalizeSaasResearchResult(response.research));
          setMode('result');
        })
        .catch((err) => {
          setMode('input');
          setError(safeBrandResearchError(err, 'Saved brand research could not be loaded. Try again.'));
        });
      return;
    }
    const brand = route?.query?.brand;
    if (!brand) return;
    setMode('progress');
    setProgress({ label: 'Loading saved research…', steps: {} });
    api.get(`/api/library/${encodeURIComponent(brand)}/brand-research`)
      .then((r) => {
        const d = r.data || {};
        d.slug = d.slug || brand;
        d._source = d._source || d.company?.website || '';
        setData(d);
        setMode('result');
      })
      .catch((err) => {
        setMode('input');
        setError(safeBrandResearchError(err, 'Saved brand research could not be loaded. Try again.'));
      });
  }, [isSaasSession, companyId, route?.query?.brand, route?.query?.research]);

  useEffect(() => {
    if (!isSaasSession || !route?.query?.brand || !brands || !researches) return;
    const brand = brands.find((item) => item.slug === route.query.brand);
    const research = researches.find((item) => item.brand?.id === brand?.id);
    if (research) navigate(`brand-research?research=${encodeURIComponent(research.id)}`);
  }, [isSaasSession, route?.query?.brand, brands, researches, navigate]);

  function selectBrand(brandId) {
    setSelectedBrandId(brandId);
    const brand = brands?.find((item) => item.id === brandId);
    setUrl(brand?.websiteUrl || '');
  }

  function reset() {
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
    setMode('input');
    setError('');
    setData(null);
    setUrl('');
    if (isSaasSession) {
      const first = brands?.[0];
      setSelectedBrandId(first?.id || '');
      setUrl(first?.websiteUrl || '');
    }
    setProgress({ label: 'Starting…', steps: {} });
    if (isSaasSession || route?.query?.brand || route?.query?.research) navigate('brand-research');
  }

  async function submit(e) {
    e?.preventDefault?.();
    if (!canGenerate) {
      setError('Your company role can view saved research but cannot run new research.');
      return;
    }
    let trimmed = url.trim();
    if (!trimmed) return;
    trimmed = trimmed.replace(/^https?\/\//i, (m) => m.replace('//', '://'));
    if (!/^https?:\/\//i.test(trimmed)) trimmed = `https://${trimmed}`;
    setMode('progress');
    setError('');
    setProgress({ label: 'Starting brand research', steps: { 'Starting brand research': 'active' } });

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const token = getToken();
      const res = await fetch(isSaasSession ? '/api/saas/brand-research' : '/api/brand-research', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(isSaasSession && companyId ? { 'x-company-id': companyId } : {}),
        },
        body: JSON.stringify(isSaasSession ? { brandId: selectedBrandId, url: trimmed } : { url: trimmed }),
        signal: ctrl.signal,
      });
      if (res.status === 401) {
        const next = encodeURIComponent(window.location.href);
        window.location.href = `/login?next=${next}`;
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const responseError = new Error('Brand research request failed');
        responseError.code = body.error;
        responseError.status = res.status;
        throw responseError;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = frame.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }

          if (evt.type === 'progress') {
            setProgress((prev) => {
              const steps = { ...prev.steps };
              const match = STEP_LABELS.find((l) => evt.label.toLowerCase().includes(l.toLowerCase()));
              if (match) {
                Object.keys(steps).forEach((k) => { if (steps[k] === 'active') steps[k] = 'done'; });
                steps[match] = evt.label.toLowerCase().includes('done') ? 'done' : 'active';
              }
              return { label: evt.label, steps };
            });
          } else if (evt.type === 'error') {
            throw new Error(evt.error);
          } else if (evt.type === 'result') {
            const done = {};
            STEP_LABELS.forEach((l) => { done[l] = 'done'; });
            setProgress({ label: 'Done', steps: done });
            const d = evt.research
              ? normalizeSaasResearchResult(evt.research)
              : (evt.brain || {});
            setData(d);
            if (evt.research?.id) {
              window.history.replaceState(
                null,
                '',
                `#/brand-research?research=${encodeURIComponent(evt.research.id)}`,
              );
            }
            setTimeout(() => setMode('result'), 350);
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(safeBrandResearchError(err));
      setMode('input');
    }
  }

  if (mode === 'result' && data) {
    return <ResultView data={data} onReset={reset} navigate={navigate} companyId={companyId} />;
  }

  const loadingSaasData = isSaasSession && (brands == null || researches == null);

  return (
    <div className="brand-research-page" style={{ padding: '32px 40px 64px', maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <style>{brandResearchResponsiveStyles}</style>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
          fontSize: 40, fontWeight: 400, margin: 0, color: PALETTE.ink, letterSpacing: '-0.02em',
        }}>Brand research</h1>
        <Pill tone="accent">Research</Pill>
      </header>

      {error && (
        <div style={{
          background: PALETTE.dangerSoft, border: '1px solid #E8C9C1', color: '#8B2E1A',
          borderRadius: 12, padding: '12px 16px', fontSize: 13, marginBottom: 20,
        }}>{error}</div>
      )}

      {loadingSaasData ? (
        <ProtectedPageLoader
          title="Loading brand research"
          body="Checking your company brands and saved research before showing this workspace."
        />
      ) : mode === 'input' ? (
        <InputView
          url={url}
          setUrl={setUrl}
          onSubmit={submit}
          brands={brands}
          researches={researches}
          isSaasSession={isSaasSession}
          selectedBrandId={selectedBrandId}
          onSelectBrand={selectBrand}
          canGenerate={canGenerate}
          navigate={navigate}
        />
      ) : null}
      {mode === 'progress' && <ProgressView progress={progress} url={url} />}
    </div>
  );
}

// ─── INPUT ────────────────────────────────────────────────────────────────
export function InputView({
  url,
  setUrl,
  onSubmit,
  brands,
  researches,
  isSaasSession,
  selectedBrandId,
  onSelectBrand,
  canGenerate = true,
  navigate,
}) {
  const existing = isSaasSession ? (researches || []) : (brands || []);
  return (
    <div>
      <Card style={{ padding: 32, textAlign: 'center', marginBottom: 28 }}>
        <h2 style={{
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontWeight: 400,
          fontSize: 28, margin: '0 0 8px', color: PALETTE.ink, letterSpacing: '-0.01em',
        }}>Build a brand research</h2>
        <p style={{ color: PALETTE.ink2, fontSize: 14, margin: '0 0 24px' }}>
          Enter the client's website. Our research agent analyzes brand identity, pulls reviews, and builds a complete profile.
        </p>
        {isSaasSession && brands?.length === 0 ? (
          <div style={{ display: 'grid', justifyItems: 'center', gap: 12 }}>
            <div style={{ color: PALETTE.ink2, fontSize: 13 }}>Create a company brand before starting research.</div>
            <Button type="button" variant="accent" onClick={() => navigate('company')}>Open company setup</Button>
          </div>
        ) : !canGenerate ? (
          <div role="status" style={{
            maxWidth: 680, margin: '0 auto', padding: '14px 16px',
            color: PALETTE.ink2, background: PALETTE.surfaceAlt,
            border: `1px solid ${PALETTE.line}`, borderRadius: 10, fontSize: 13,
          }}>
            Your company role can view saved research but cannot run new research.
          </div>
        ) : (
        <form className="brand-research-form" onSubmit={onSubmit} style={{ display: 'flex', gap: 8, maxWidth: 680, margin: '0 auto' }}>
          {isSaasSession && (
            <select
              aria-label="Company brand"
              value={selectedBrandId}
              onChange={(event) => onSelectBrand(event.target.value)}
              required
              style={{
                minWidth: 190, padding: '12px 14px', fontSize: 14,
                fontFamily: 'Inter, sans-serif', color: PALETTE.ink,
                background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
                borderRadius: 10,
              }}
            >
              {brands?.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
            </select>
          )}
          <input
            aria-label="Website URL" type="url" required autoComplete="url"
            placeholder="https://ottawageneralcontractors.com"
            value={url} onChange={(e) => setUrl(e.target.value)}
            style={{
              flex: 1, padding: '12px 14px', fontSize: 14,
              fontFamily: 'Inter, sans-serif', color: PALETTE.ink,
              background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
              borderRadius: 10, outline: 'none',
            }}
          />
          <Button type="submit" variant="accent">Analyze →</Button>
        </form>
        )}
      </Card>

      <div>
        <Eyebrow style={{ marginBottom: 12 }}>Existing research</Eyebrow>
        {brands == null || (isSaasSession && researches == null) ? (
          <div style={{ color: PALETTE.ink3, fontSize: 13, fontStyle: 'italic' }}>Loading…</div>
        ) : existing.length === 0 ? (
          <div style={{ color: PALETTE.ink3, fontSize: 13, fontStyle: 'italic' }}>Nothing yet — analyze a URL above.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {existing.map((item) => {
              const brand = isSaasSession ? item.brand : item;
              const href = isSaasSession
                ? `#/brand-research?research=${encodeURIComponent(item.id)}`
                : `#/brand-research?brand=${encodeURIComponent(brand.slug)}`;
              const subtitle = isSaasSession
                ? [item.summary?.location, item.generatedAt ? new Date(item.generatedAt).toLocaleDateString() : ''].filter(Boolean).join(' · ')
                : (brand.location || brand.website || '');
              return (
              <a key={item.id || brand.slug} href={href} style={{
                display: 'flex', gap: 10, alignItems: 'center',
                padding: '12px 14px', background: PALETTE.surface,
                border: `1px solid ${PALETTE.line}`, borderRadius: 12,
                textDecoration: 'none', color: 'inherit',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: PALETTE.surfaceAlt,
                  border: `1px solid ${PALETTE.line}`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontWeight: 700, color: PALETTE.accent, flexShrink: 0,
                }}>{(brand.name || '?').trim().charAt(0).toUpperCase()}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{brand.name}</div>
                  <div style={{ fontSize: 11, color: PALETTE.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
                </div>
              </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PROGRESS ─────────────────────────────────────────────────────────────
export function ProgressView({ progress, url }) {
  const host = (url || '').replace(/^https?:\/\//, '').split('/')[0];
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px', borderBottom: `1px solid ${PALETTE.line}`,
      }}>
        <Beacon size="inline" delayMs={0} slowAfterMs={null} />
        <span style={{ fontSize: 14, fontWeight: 500, color: PALETTE.ink }}>{progress.label}</span>
        {host && <span style={{ marginLeft: 'auto', fontSize: 12, color: PALETTE.ink3 }}>{host}</span>}
      </div>
      <div>
        {STEP_LABELS.map((label) => {
          const state = progress.steps[label];
          const active = state === 'active';
          const done = state === 'done';
          return (
            <div key={label} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 18px', borderBottom: `1px solid ${PALETTE.lineSoft}`,
              fontSize: 13, color: active ? PALETTE.ink : done ? PALETTE.ink2 : PALETTE.ink3,
            }}>
              <span style={{
                width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                border: `1.5px solid ${done || active ? PALETTE.accent : PALETTE.line}`,
                background: done ? PALETTE.accent : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 9, fontWeight: 900,
              }}>{done ? '✓' : ''}</span>
              <span>{label}</span>
              <span style={{
                marginLeft: 'auto', fontSize: 10, textTransform: 'uppercase',
                letterSpacing: '0.1em', fontWeight: 700,
                color: active || done ? PALETTE.accent : 'transparent',
              }}>{active ? 'Running' : done ? 'Done' : ''}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── DERIVED HELPERS ──────────────────────────────────────────────────────
// Each falls back to a client-side compute when the backend doesn't yet
// return the field. Recommended to move these to the backend.

// 0–100 brand-strength score from 12 completeness signals.
function computeBrandScore(d) {
  const checks = [
    !!d?.company?.mission,
    (d?.company?.certifications?.length || 0) >= 3,
    (d?.company?.awards?.length || 0) >= 1,
    (d?.marketingAngles?.length || 0) >= 3,
    !!d?.brand?.voice,
    (d?.services?.primary?.length || 0) >= 3,
    !!(d?.commercial?.priceRange?.low && d?.commercial?.priceRange?.high),
    (d?.reputation?.reviewCount || 0) > 0,
    (d?.reputation?.testimonials?.length || 0) > 0,
    (d?.social?.length || 0) >= 3,
    !!d?.targetCustomer?.demographic,
    (d?.contentGaps?.filter((g) => g.severity === 'High').length || 0) === 0,
  ];
  const passed = checks.filter(Boolean).length;
  return Math.round((passed / checks.length) * 100);
}

function shortVoice(d) {
  const t = d?.brand?.tone;
  if (!t || !t.length) return '—';
  return t.slice(0, 3).join(' · ');
}

function angleStrength(idx) { return Math.max(40, 95 - idx * 7); }

function gapAction(gap) {
  const g = (gap?.gap || '').toLowerCase();
  if (g.includes('pricing') || g.includes('budget'))     return 'Generate cost-guide content';
  if (g.includes('testimonial') || g.includes('review')) return 'Build review-collection funnel';
  if (g.includes('financing'))                            return 'Create financing landing page';
  if (g.includes('faq') || g.includes('process'))         return 'Generate FAQ block';
  if (g.includes('warranty') || g.includes('guarantee'))  return 'Write warranty explainer';
  return 'Generate content';
}

function displayUrl(u) {
  if (!u) return '';
  return u.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

// ─── RESULT (V02 INSIGHTS) ────────────────────────────────────────────────
function ResultView({ data, onReset, navigate, companyId }) {
  const [open, setOpen] = useState('angles');
  const c = data.company || {};
  const highGaps = (data.contentGaps || []).filter((g) => g.severity === 'High').length;
  const totalGaps = (data.contentGaps || []).length;
  const brandScore = computeBrandScore(data);
  const topAngle = data.marketingAngles?.[0];
  const hasReviews = (data.reputation?.reviewCount || 0) > 0;
  const generatedAt = new Date(data._saasResearch?.generatedAt || Date.now())
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  async function downloadMarkdown() {
    const markdownArtifactId = data._saasResearch?.artifacts?.markdownArtifactId;
    if (markdownArtifactId) {
      const response = await api.get(
        `/api/saas/artifacts/${encodeURIComponent(markdownArtifactId)}/signed-url`,
        tenantHeaders(companyId),
      );
      const a = document.createElement('a');
      a.href = response.signedUrl.url;
      a.rel = 'noopener';
      a.click();
      return;
    }
    const blob = new Blob([data.markdown || ''], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${data.slug || 'brand-research'}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  return (
    <div className="brand-research-result" style={{ height: '100%', overflowY: 'auto', padding: '26px 32px 40px', fontFamily: 'Inter, sans-serif' }}>
      <style>{brandResearchResponsiveStyles}</style>
      {/* Header */}
      <div className="brand-research-result-header" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="brand-research-result-identity" style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0, flex: '1 1 420px' }}>
          <BrandLogoMark name={c.name || '—'} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Eyebrow style={{ marginBottom: 4 }}>
              Brand Research · {generatedAt}
            </Eyebrow>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <div className="brand-research-result-title" style={{ fontFamily: 'Instrument Serif, serif', fontSize: 32, color: PALETTE.ink, lineHeight: 1, letterSpacing: '-0.01em' }}>
                {c.name || data.slug}
              </div>
              <div className="brand-research-result-meta" style={{ fontSize: 12.5, color: PALETTE.ink3 }}>
                {c.location || c.address || ''}
                {c.website && (
                  <>
                    {' · '}
                    <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{displayUrl(c.website)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="brand-research-result-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button variant="ghost" size="sm" onClick={onReset}>Re-run</Button>
          {data.markdown && <Button variant="ghost" size="sm" onClick={downloadMarkdown}>Download</Button>}
          <Button variant="primary" size="sm" onClick={() => navigate('marketing-brief')}>Use in campaign</Button>
        </div>
      </div>

      {/* Insight cards */}
      <div className="brand-research-insight-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        <InsightCard
          eyebrow="BRAND STRENGTH"
          metric={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Ring value={brandScore} size={48} stroke={4} />
              <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 36, color: PALETTE.ink, lineHeight: 1 }}>
                {brandScore}<span style={{ fontSize: 16, color: PALETTE.ink3 }}>/100</span>
              </div>
            </div>
          }
          sub="Computed from completeness of brand signals."
          onClick={() => setOpen('strength')}
        />
        <InsightCard
          eyebrow="LEAD ANGLE"
          metric={
            <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 22, color: PALETTE.ink, lineHeight: 1.1 }}>
              {topAngle?.hook || '—'}
            </div>
          }
          sub="Highest-ranked hook from marketing angles."
          chip={topAngle ? <Tag tone="accent" size="sm">{angleStrength(0)} strength</Tag> : null}
          onClick={() => setOpen('angles')}
        />
        <InsightCard
          eyebrow="REPUTATION"
          metric={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 12, height: 12, borderRadius: 999,
                background: hasReviews ? PALETTE.success : PALETTE.danger,
                boxShadow: `0 0 0 4px ${hasReviews ? PALETTE.successSoft : PALETTE.dangerSoft}`,
              }}/>
              <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 22, color: PALETTE.ink, lineHeight: 1.1 }}>
                {hasReviews ? `${data.reputation.rating}★` : 'No data found'}
              </div>
            </div>
          }
          sub={hasReviews
            ? `${data.reputation.reviewCount} reviews on ${data.reputation.platform}`
            : '0 reviews surfaced. Public reputation is a measurable gap.'}
          chip={hasReviews ? null : <Tag tone="danger" size="sm">Action needed</Tag>}
          onClick={() => setOpen('reputation')}
        />
        <InsightCard
          eyebrow="CONTENT GAPS"
          metric={
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 36, color: PALETTE.ink, lineHeight: 1 }}>{highGaps}</div>
              <div style={{ fontSize: 13, color: PALETTE.ink3 }}>high-severity</div>
            </div>
          }
          sub={`${totalGaps} total · ${totalGaps - highGaps} medium/low`}
          chip={highGaps > 0 ? <Tag tone="warn" size="sm">{highGaps} to fix</Tag> : null}
          onClick={() => setOpen('gaps')}
        />
      </div>

      {/* Quick facts strip */}
      <div style={{
        display: 'flex', gap: 28, padding: '12px 18px', marginBottom: 22, flexWrap: 'wrap',
        background: PALETTE.surfaceAlt, border: `1px solid ${PALETTE.line}`, borderRadius: 12,
      }}>
        {c.founded && <Fact label="Founded" value={c.founded} />}
        <Fact label="Service area" value={`${(data.areas || []).length} cities`} />
        {data.commercial?.priceRange?.low && (
          <Fact label="Pricing" value={`${data.commercial.priceRange.low} – ${data.commercial.priceRange.high}`} />
        )}
        {data.commercial?.priceRange?.tier && <Fact label="Tier" value={data.commercial.priceRange.tier} />}
        {(data.commercial?.warranties?.length || 0) > 0 && <Fact label="Warranty" value="Yes" />}
        {data.commercial?.financing?.offered && <Fact label="Financing" value={data.commercial.financing.offered} />}
        <Fact label="Voice" value={shortVoice(data)} />
      </div>

      {/* Drill-downs */}
      <DrillSection
        eyebrow="MARKETING ANGLES" title="Hooks worth running" sub="Ranked by signal strength"
        open={open === 'angles'} onToggle={() => setOpen(open === 'angles' ? null : 'angles')}
        count={(data.marketingAngles || []).length}
      >
        <div className="brand-research-two-column" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {(data.marketingAngles || []).map((a, i) => <AngleMini key={i} angle={a} idx={i} />)}
        </div>
      </DrillSection>

      <DrillSection
        eyebrow="CONTENT GAPS" title="What to build next" sub="Generate this content from the platform"
        open={open === 'gaps'} onToggle={() => setOpen(open === 'gaps' ? null : 'gaps')}
        count={(data.contentGaps || []).length}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(data.contentGaps || []).map((g, i) => (
            <div key={i} className="brand-research-gap-row" style={{
              display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr) minmax(180px, 220px) minmax(120px, 140px)',
              alignItems: 'center', gap: 14, padding: '12px 14px', background: '#FFF',
              borderTopWidth: 1, borderRightWidth: 1, borderBottomWidth: 1, borderLeftWidth: 3,
              borderTopStyle: 'solid', borderRightStyle: 'solid', borderBottomStyle: 'solid', borderLeftStyle: 'solid',
              borderTopColor: PALETTE.line, borderRightColor: PALETTE.line, borderBottomColor: PALETTE.line,
              borderLeftColor: g.severity === 'High' ? PALETTE.sevHigh : g.severity === 'Medium' ? PALETTE.sevMed : PALETTE.sevLow,
              borderRadius: 10,
            }}>
              <SeverityDot sev={g.severity} />
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 500, color: PALETTE.ink, marginBottom: 2 }}>{g.gap}</div>
                <div style={{ fontSize: 12, color: PALETTE.ink3, lineHeight: 1.4 }}>{g.detail}</div>
              </div>
              <div style={{ fontSize: 11.5, color: PALETTE.ink2, lineHeight: 1.4 }}>
                {g.action || gapAction(g)}
              </div>
              <Button variant="ghost" size="sm">Generate →</Button>
            </div>
          ))}
        </div>
      </DrillSection>

      <DrillSection
        eyebrow="REPUTATION" title="What the public is saying" sub="Reviews, testimonials, owner responsiveness"
        open={open === 'reputation'} onToggle={() => setOpen(open === 'reputation' ? null : 'reputation')}
      >
        {hasReviews ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Fact label="Rating" value={`${data.reputation.rating}★ (${data.reputation.reviewCount})`} />
            <Fact label="Platform" value={data.reputation.platform} />
            <Fact label="Owner responds" value={data.reputation.ownerResponds || 'Unknown'} />
          </div>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 18, padding: '20px 24px',
            background: PALETTE.surfaceAlt, border: `1px dashed ${PALETTE.line}`, borderRadius: 12,
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: PALETTE.dangerSoft,
              display: 'grid', placeItems: 'center', color: PALETTE.danger, fontSize: 24, fontWeight: 700,
            }}>!</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 22, color: PALETTE.ink, lineHeight: 1.2, marginBottom: 4 }}>
                No public reviews found.
              </div>
              <div style={{ fontSize: 13, color: PALETTE.ink2, lineHeight: 1.5 }}>
                {data.reputation?.note || 'The site references a Testimonials page but no actual reviews surfaced. This is the single highest-leverage gap to close.'}
              </div>
            </div>
            <Button variant="primary" size="sm">Build review funnel</Button>
          </div>
        )}
      </DrillSection>

      <DrillSection
        eyebrow="WHO THEY SELL TO" title="Target customer"
        open={open === 'audience'} onToggle={() => setOpen(open === 'audience' ? null : 'audience')}
      >
        <div className="brand-research-two-column" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Card style={{ padding: 16 }}>
            <Eyebrow style={{ marginBottom: 6 }}>Demographic</Eyebrow>
            <div style={{ fontSize: 13, color: PALETTE.ink2, lineHeight: 1.5 }}>
              {data.targetCustomer?.demographic || '—'}
            </div>
          </Card>
          <Card style={{ padding: 16 }}>
            <Eyebrow style={{ marginBottom: 6 }}>Psychographic</Eyebrow>
            <div style={{ fontSize: 13, color: PALETTE.ink2, lineHeight: 1.5 }}>
              {data.targetCustomer?.psychographic || '—'}
            </div>
          </Card>
        </div>
        {(data.targetCustomer?.painPoints || []).length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Eyebrow style={{ marginBottom: 8 }}>Pain points</Eyebrow>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: PALETTE.ink2, lineHeight: 1.6 }}>
              {data.targetCustomer.painPoints.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}
      </DrillSection>
    </div>
  );
}

// ─── PRIMITIVES (V02) ─────────────────────────────────────────────────────
function Eyebrow({ children, color, style = {} }) {
  return (
    <div style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: color || PALETTE.accent, fontWeight: 500,
      ...style,
    }}>{children}</div>
  );
}

function Tag({ children, tone = 'neutral', size = 'md' }) {
  const tones = {
    neutral: { bg: PALETTE.surfaceAlt, fg: PALETTE.ink2, bd: PALETTE.line },
    accent:  { bg: PALETTE.accentTint, fg: PALETTE.accentDeep, bd: PALETTE.accentSoft },
    soft:    { bg: '#FFF', fg: PALETTE.ink2, bd: PALETTE.line },
    success: { bg: PALETTE.successSoft, fg: PALETTE.success, bd: '#CFD9B4' },
    warn:    { bg: PALETTE.warnSoft, fg: PALETTE.warn, bd: '#EBDCB2' },
    danger:  { bg: PALETTE.dangerSoft, fg: PALETTE.danger, bd: '#E8C9C1' },
  };
  const t = tones[tone];
  const sizes = {
    sm: { padding: '2px 8px',  fontSize: 10.5 },
    md: { padding: '4px 10px', fontSize: 11.5 },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      ...sizes[size],
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      borderRadius: 999, fontFamily: 'Inter, sans-serif',
      fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function SeverityDot({ sev }) {
  const c = sev === 'High' ? PALETTE.sevHigh : sev === 'Medium' ? PALETTE.sevMed : PALETTE.sevLow;
  return <span style={{ width: 8, height: 8, borderRadius: 999, background: c, display: 'inline-block' }}/>;
}

function Ring({ value = 80, size = 64, stroke = 5, color }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={PALETTE.lineSoft} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color || PALETTE.accent} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off}
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
}

function InsightCard({ eyebrow, metric, sub, chip, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: '#FFF', border: `1px solid ${PALETTE.line}`, borderRadius: 12,
      padding: 16, textAlign: 'left', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
      display: 'flex', flexDirection: 'column', gap: 10, minHeight: 132,
      transition: 'border-color .15s',
    }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = PALETTE.accentSoft; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = PALETTE.line; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Eyebrow>{eyebrow}</Eyebrow>
        {chip}
      </div>
      <div>{metric}</div>
      <div style={{ fontSize: 12, color: PALETTE.ink3, lineHeight: 1.4, flex: 1 }}>{sub}</div>
    </button>
  );
}

function Fact({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: PALETTE.ink3, fontFamily: 'Inter, sans-serif' }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 500, color: PALETTE.ink }}>{value}</div>
    </div>
  );
}

function DrillSection({ eyebrow, title, sub, count, open, onToggle, children }) {
  return (
    <div style={{
      background: open ? '#FFF' : 'transparent',
      borderTopWidth: 1,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderLeftWidth: 1,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: PALETTE.line,
      borderRightColor: open ? PALETTE.line : 'transparent',
      borderBottomColor: open ? PALETTE.line : 'transparent',
      borderLeftColor: open ? PALETTE.line : 'transparent',
      borderRadius: open ? 12 : 0,
      marginBottom: open ? 14 : 0,
      overflow: 'hidden',
    }}>
      <button onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%',
        padding: open ? '16px 20px 8px' : '16px 6px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        textAlign: 'left', fontFamily: 'Inter, sans-serif',
      }}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 22, color: PALETTE.ink, lineHeight: 1, letterSpacing: '-0.005em' }}>
          {title}
        </div>
        {sub && <div style={{ fontSize: 12, color: PALETTE.ink3 }}>{sub}</div>}
        {count !== undefined && (
          <span style={{
            padding: '2px 8px', fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
            background: PALETTE.surfaceAlt, color: PALETTE.ink2, border: `1px solid ${PALETTE.line}`,
            borderRadius: 999,
          }}>{count}</span>
        )}
        <div style={{ flex: 1 }}/>
        <span style={{ fontSize: 11, color: PALETTE.ink3, fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 4 }}>
          {open ? 'Collapse' : 'Expand'}
          <span style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s' }}>›</span>
        </span>
      </button>
      {open && <div style={{ padding: '8px 20px 20px' }}>{children}</div>}
    </div>
  );
}

function AngleMini({ angle, idx }) {
  const strength = angle.strength ?? angleStrength(idx);
  const tags = angle.tags || [];
  return (
    <div style={{
      background: '#FFF', border: `1px solid ${PALETTE.line}`,
      borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 16, color: PALETTE.ink, lineHeight: 1.2 }}>
          {angle.hook}
        </div>
        <Tag tone="accent" size="sm">{strength}</Tag>
      </div>
      <div style={{ fontSize: 12, color: PALETTE.ink2, lineHeight: 1.4 }}>{angle.detail}</div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {tags.map((t) => <Tag key={t} tone="soft" size="sm">{t}</Tag>)}
        </div>
      )}
    </div>
  );
}

function BrandLogoMark({ name }) {
  const initials = name.split(' ').slice(0, 2).map((n) => n[0] || '').join('').toUpperCase();
  return (
    <div style={{
      width: 56, height: 56, borderRadius: 12,
      background: PALETTE.ink, color: PALETTE.accent,
      display: 'grid', placeItems: 'center',
      fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
      fontSize: 28, letterSpacing: '-0.02em', flexShrink: 0,
    }}>{initials}</div>
  );
}
